// ============================================================
// BufferTrendTracker — delivery-caused buffer drain, isolated
// ============================================================
//
// The ABR policy gives up a rendition when the buffer is losing ground. Raw
// buffer change is the wrong signal for that, because the LL latency controller
// moves the buffer on purpose and has nothing to do with delivery:
//
//   * catch-up plays at 1.08x and slow-down at 0.92x, so the buffer shrinks or
//     grows by 0.08 s per second while delivery is exactly on time;
//   * when latency exceeds the jump threshold the controller seeks forward,
//     which removes seconds of buffer in one step.
//
// Reading either of those as "the network cannot keep up" would drop quality
// precisely when the player is doing its job. This tracker reports only the
// shortfall that delivery is responsible for.

export interface BufferTrendSample {
  /** Contiguous buffer ahead of the playhead, in seconds. */
  bufferSeconds: number;
  /** Media position, used to tell a seek apart from ordinary playback. */
  currentTime: number;
  playbackRate: number;
  nowMs: number;
}

export interface BufferTrendOptions {
  /** Minimum baseline before a trend is recomputed (seconds). */
  baselineSeconds?: number;
  /**
   * How far the playhead may deviate from `elapsed * playbackRate` before the
   * movement is treated as a seek or a stall rather than playback.
   */
  seekToleranceSeconds?: number;
}

const DEFAULTS: Required<BufferTrendOptions> = {
  baselineSeconds: 1,
  seekToleranceSeconds: 0.5,
};

interface Mark {
  bufferSeconds: number;
  currentTime: number;
  atMs: number;
}

export class BufferTrendTracker {
  private readonly opts: Required<BufferTrendOptions>;
  private mark: Mark | null = null;
  private trend = 0;

  constructor(options: BufferTrendOptions = {}) {
    this.opts = { ...DEFAULTS, ...options };
  }

  reset(): void {
    this.mark = null;
    this.trend = 0;
  }

  /**
   * Returns seconds of buffer gained or lost per second through delivery alone.
   * Negative means the stream is arriving slower than it is being consumed.
   */
  sample(sample: BufferTrendSample): number {
    const { bufferSeconds, currentTime, nowMs } = sample;
    const rate = sample.playbackRate > 0 ? sample.playbackRate : 1;
    const mark = this.mark;
    if (!mark) {
      this.mark = { bufferSeconds, currentTime, atMs: nowMs };
      return 0;
    }

    const elapsed = (nowMs - mark.atMs) / 1000;
    if (elapsed < this.opts.baselineSeconds) return this.trend;

    // A playhead that moved by anything other than elapsed * rate was seeked
    // (latency jump, gap watchdog) or was frozen (stall). Either way the buffer
    // delta across that boundary says nothing about delivery, so start over.
    const played = currentTime - mark.currentTime;
    if (Math.abs(played - elapsed * rate) > this.opts.seekToleranceSeconds) {
      this.mark = { bufferSeconds, currentTime, atMs: nowMs };
      this.trend = 0;
      return 0;
    }

    // Playing off-speed moves the buffer by design; add that part back so only
    // the delivery shortfall remains.
    const observed = (bufferSeconds - mark.bufferSeconds) / elapsed;
    this.trend = observed + (rate - 1);
    this.mark = { bufferSeconds, currentTime, atMs: nowMs };
    return this.trend;
  }
}
